import React, { useMemo, useState } from "react";
import "../features.css";
import { useAuth } from "../../auth/AuthProvider";
import { tierCan } from "../../auth/permissions";
import { agentMatches, allSplitsFor, ownSplitFor, useBookData } from "../../data/BookDataProvider";
import { COMMISSION_RATE, PREMIUM_SANITY_CAP, formatMoney, parsePremium } from "../../data/format";
import {
  Badge,
  Button,
  Card,
  ChevronDownIcon,
  ChevronRightIcon,
  Combobox,
  DataTable,
  DollarIcon,
  EmptyState,
  Segmented,
  SkeletonText,
  StatTile
} from "../../ui";
import { LobBadge } from "../shared";

// The five ordered attribution slots on every policy (the deployed
// convention). Used for the by-agent rollup so each participant keeps their
// Agent record — and therefore their tier — for the E-reviewed tier badges.
const SPLIT_SLOTS = [
  ["mainAgent", "mainAgentSplit"],
  ["refAgentOne", "refAgentOneSplit"],
  ["refAgentTwo", "refAgentTwoSplit"],
  ["refAgentThree", "refAgentThreeSplit"],
  ["refAgentFour", "refAgentFourSplit"]
];

/**
 * Commissions (REDESIGN-RUNBOOK §3.11 "the split model is the business
 * model"; E REFERRAL-UX-SPEC §7; user story R10). A computed-v1 REPORT page:
 * no new backend — every figure is derived client-side from the already
 * scoped Auto/Home/Umbrella policy records (Coordinator 2026-07-12: A/H/U
 * only; OtherPolicy has no split columns and Orders aren't in the data layer,
 * so both are out of v1).
 *
 * Tier-forked, gated on tierCan(tier).seeOtherSplits (UX only — the backend
 * already scopes the lists):
 *   • REFERRAL → their own lines only, "Your share" language, by-month
 *     rollup, per E §7 (complete, not redacted; no gross book total).
 *   • WRITING/ADMIN → gross book estimate (2.5% before splits) + every
 *     participant's split + a by-agent rollup.
 *
 * Honesty guardrails reused verbatim from the deployed convention: the legacy
 * 2.5% estimate (COMMISSION_RATE), the P1 garbage-premium guard
 * (PREMIUM_SANITY_CAP — implausible premiums are excluded from sums and the
 * exclusion is surfaced, never silently summed), a "~" on every estimate, and
 * the single "your commission statement is the source of truth" footnote.
 *
 * Shipping this real page ALSO closes QA finding M-2: /app/commissions is no
 * longer a phase-marked PlaceholderPage that bounced referral agents into the
 * legacy dashboard (behind FEATURES.commissions; see App.js/navConfig).
 */

const MONTH_LABEL = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric",
  timeZone: "UTC"
});

const LOB_OPTIONS = [
  { value: "all", label: "All" },
  { value: "auto", label: "Auto" },
  { value: "home", label: "Home" },
  { value: "umbrella", label: "Umbrella" }
];

/** The month a policy's commission belongs to — its effective month (the only
    reliable per-policy date; no booked/paid-date field exists). Undated
    policies fall into their own honest bucket rather than being dropped. */
function monthKeyFor(policy) {
  if (!policy.effectiveDate) {
    return null;
  }
  const date = new Date(policy.effectiveDate);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function monthLabelFor(key) {
  if (key === "undated") {
    return "Undated";
  }
  const [year, month] = key.split("-").map(Number);
  return MONTH_LABEL.format(new Date(Date.UTC(year, month - 1, 1)));
}

function triggerCsvDownload(filename, rows) {
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = rows.map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** One expandable month: summary head + per-policy lines when open. */
function MonthGroup({ group, expanded, onToggle, renderLine }) {
  return (
    <div className="feat-comm-month">
      <button
        type="button"
        className="feat-comm-month__head"
        aria-expanded={expanded}
        onClick={onToggle}
      >
        <span className="feat-comm-month__chev">
          {expanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        </span>
        <span>
          <span className="feat-comm-month__label">{group.label}</span>{" "}
          <span className="feat-comm-month__meta">
            {group.count} polic{group.count === 1 ? "y" : "ies"} · {formatMoney(group.premium)}{" "}
            premium
            {group.implausible > 0 ? ` · ${group.implausible} excluded` : ""}
          </span>
        </span>
        <span className="feat-comm-month__spacer" />
        <span className="feat-comm-month__amount">~{formatMoney(group.amount)}</span>
      </button>
      {expanded ? <div className="feat-comm-month__body">{group.rows.map(renderLine)}</div> : null}
    </div>
  );
}

function CommissionsPage() {
  const { identity, tier, agents } = useAuth();
  const can = tierCan(tier);
  const { loading, error, households, refresh } = useBookData();

  const [lob, setLob] = useState("all");
  const [agentFilter, setAgentFilter] = useState(""); // staff only; "" = all agents
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleMonth = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  // One row per active A/H/U policy the caller can see a commission on.
  // Referral tier keeps only policies they hold a split on (ownSplitFor);
  // staff keep every policy (gross book) with all participants.
  const rows = useMemo(() => {
    const out = [];
    for (const household of households) {
      for (const policy of household.activePolicies) {
        if (lob !== "all" && policy.lobKey !== lob) {
          continue;
        }
        const premium = parsePremium(policy.premium);
        // P1: an implausible premium (e.g. the phone-shaped $1.59B record)
        // must not produce a garbage commission — the row stays visible with
        // its raw premium, but its estimate is withheld and it's excluded
        // from every sum.
        const implausible = premium >= PREMIUM_SANITY_CAP;
        const base = { household, policy, premium, implausible, monthKey: monthKeyFor(policy) };

        if (can.seeOtherSplits) {
          out.push({
            ...base,
            splits: allSplitsFor(policy, agents),
            gross: implausible ? 0 : premium * COMMISSION_RATE
          });
        } else {
          const split = ownSplitFor(policy, identity);
          if (split == null) {
            continue;
          }
          out.push({
            ...base,
            split,
            share: implausible ? 0 : premium * COMMISSION_RATE * (split / 100)
          });
        }
      }
    }
    return out;
  }, [households, can.seeOtherSplits, agents, identity, lob]);

  // Staff agent filter: keep policies where the selected agent participates
  // (matched on _id OR uid, invariant #3).
  const visibleRows = useMemo(() => {
    if (!can.seeOtherSplits || !agentFilter) {
      return rows;
    }
    const selected = agents.find((a) => String(a._id) === agentFilter) || null;
    return rows.filter((r) =>
      [
        r.policy.mainAgent,
        r.policy.refAgentOne,
        r.policy.refAgentTwo,
        r.policy.refAgentThree,
        r.policy.refAgentFour
      ].some((v) => agentMatches(selected, v))
    );
  }, [rows, can.seeOtherSplits, agentFilter, agents]);

  // Month rollup, newest first, "Undated" last.
  const months = useMemo(() => {
    const map = new Map();
    for (const row of visibleRows) {
      const key = row.monthKey || "undated";
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(row);
    }
    const groups = [...map.entries()].map(([key, groupRows]) => {
      let premium = 0;
      let amount = 0;
      let implausible = 0;
      for (const r of groupRows) {
        if (r.implausible) {
          implausible += 1;
        } else {
          premium += r.premium;
          amount += can.seeOtherSplits ? r.gross : r.share;
        }
      }
      return {
        key,
        label: monthLabelFor(key),
        rows: groupRows,
        count: groupRows.length,
        premium,
        amount,
        implausible
      };
    });
    groups.sort((a, b) => {
      if (a.key === "undated") {
        return 1;
      }
      if (b.key === "undated") {
        return -1;
      }
      return b.key.localeCompare(a.key);
    });
    return groups;
  }, [visibleRows, can.seeOtherSplits]);

  // Header KPIs.
  const kpis = useMemo(() => {
    const solid = visibleRows.filter((r) => !r.implausible);
    const premium = solid.reduce((s, r) => s + r.premium, 0);
    const excluded = visibleRows.length - solid.length;
    if (can.seeOtherSplits) {
      const gross = solid.reduce((s, r) => s + r.gross, 0);
      // "Splits owed" = the portion of gross attributable to non-main
      // (referral) participants — the money the split model actually moves.
      const owed = solid.reduce((s, r) => {
        const refPct = (r.splits || [])
          .filter((p) => p.label !== "Main" && p.split != null)
          .reduce((sum, p) => sum + p.split, 0);
        return s + r.premium * COMMISSION_RATE * (refPct / 100);
      }, 0);
      return { premium, policies: visibleRows.length, gross, owed, excluded };
    }
    const share = solid.reduce((s, r) => s + r.share, 0);
    const splitVals = solid.map((r) => r.split).filter((v) => v != null);
    const avgSplit = splitVals.length
      ? Math.round(splitVals.reduce((s, v) => s + v, 0) / splitVals.length)
      : 0;
    return { premium, policies: visibleRows.length, share, avgSplit, excluded };
  }, [visibleRows, can.seeOtherSplits]);

  // Staff by-agent rollup: each participant's estimated SHARE across the
  // filtered book (E's review: a payable per agent, distinct from the gross).
  // Iterates the raw slots so each agent keeps their record — and therefore
  // their tier — for the tier badge.
  const byAgent = useMemo(() => {
    if (!can.seeOtherSplits) {
      return [];
    }
    const acc = new Map(); // key -> { name, authority, policies:Set, commission }
    for (const row of visibleRows) {
      if (row.implausible) {
        continue;
      }
      for (const [agentField, splitField] of SPLIT_SLOTS) {
        const raw = row.policy[agentField];
        const splitVal = row.policy[splitField];
        if (!raw || raw === "notTagged" || splitVal == null) {
          continue;
        }
        const agent = agents.find((a) => agentMatches(a, raw));
        const key = agent ? String(agent._id) : String(raw);
        const name = agent
          ? [agent.firstName, agent.lastName].filter(Boolean).join(" ") ||
            agent.email ||
            String(raw)
          : String(raw);
        if (!acc.has(key)) {
          acc.set(key, {
            name,
            authority: agent ? agent.authority : null,
            policies: new Set(),
            commission: 0
          });
        }
        const entry = acc.get(key);
        entry.policies.add(row.policy._id);
        entry.commission += row.premium * COMMISSION_RATE * (Number(splitVal) / 100);
      }
    }
    return [...acc.values()]
      .map((e) => ({
        name: e.name,
        authority: e.authority,
        policies: e.policies.size,
        commission: e.commission
      }))
      .sort((a, b) => b.commission - a.commission);
  }, [visibleRows, can.seeOtherSplits, agents]);

  const agentOptions = useMemo(
    () => [
      { value: "", label: "All agents" },
      ...agents
        .map((a) => ({
          value: String(a._id),
          label: [a.firstName, a.lastName].filter(Boolean).join(" ") || a.email || "Unnamed agent"
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    ],
    [agents]
  );

  const exportCsv = () => {
    const header = can.seeOtherSplits
      ? [
          "Month",
          "Household",
          "Line",
          "Carrier",
          "Policy #",
          "Premium",
          "Participants",
          "Est. gross commission"
        ]
      : [
          "Month",
          "Client",
          "Line",
          "Carrier",
          "Policy #",
          "Premium",
          "Your split %",
          "Est. your share"
        ];
    const body = visibleRows.map((r) => {
      const common = [
        r.monthKey ? monthLabelFor(r.monthKey) : "Undated",
        r.household.name,
        r.policy.lob,
        r.policy.carrier || "",
        r.policy.policyNumber || "",
        r.implausible ? `${r.premium} (excluded)` : r.premium
      ];
      return can.seeOtherSplits
        ? [
            ...common,
            (r.splits || [])
              .map((p) => `${p.name} ${p.split == null ? "?" : `${p.split}%`}`)
              .join(" | "),
            r.implausible ? "excluded" : Math.round(r.gross)
          ]
        : [...common, r.split, r.implausible ? "excluded" : Math.round(r.share)];
    });
    triggerCsvDownload(can.seeOtherSplits ? "commissions-staff.csv" : "commissions.csv", [
      header,
      ...body
    ]);
  };

  const renderLine = (row) => (
    <div key={row.policy._id} className="feat-comm-line">
      <LobBadge lobKey={row.policy.lobKey} lob={row.policy.lob} />
      <span className="feat-comm-line__name">{row.household.name}</span>
      <span className="feat-comm-line__detail">
        {[row.policy.carrier || "Unknown carrier", `${formatMoney(row.policy.premium)}/yr`].join(
          " · "
        )}
        {can.seeOtherSplits && row.splits && row.splits.length ? (
          <>
            {" · "}
            {row.splits.map((p) => `${p.name}${p.split == null ? "" : ` ${p.split}%`}`).join(" · ")}
          </>
        ) : null}
      </span>
      <span className="feat-comm-line__spacer" />
      {row.implausible ? (
        <span className="feat-comm-line__amount fk-muted">excluded</span>
      ) : can.seeOtherSplits ? (
        <span className="feat-comm-line__amount">~{formatMoney(row.gross)}</span>
      ) : (
        <span className="feat-d2row__share">
          {row.split}% ≈ {formatMoney(row.share)}/yr
        </span>
      )}
    </div>
  );

  if (error) {
    return (
      <div className="shell-page">
        <div className="shell-page__titlerow">
          <h1 className="shell-page__title">Commissions</h1>
        </div>
        <Card>
          <EmptyState
            title="Something went wrong"
            message={error}
            action={
              <Button variant="primary" onClick={refresh}>
                Try again
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const hasRows = visibleRows.length > 0;

  return (
    <div className="shell-page">
      <div className="shell-page__titlerow">
        <h1 className="shell-page__title">Commissions</h1>
        {hasRows ? (
          <Button variant="secondary" onClick={exportCsv}>
            Export CSV
          </Button>
        ) : null}
      </div>

      {/* KPI row (E §7 header stats for referral; gross + splits for staff). */}
      <div className="feat-stats" style={{ marginBottom: "var(--space-5)" }}>
        {can.seeOtherSplits ? (
          <>
            <StatTile label="Written premium" value={loading ? "…" : formatMoney(kpis.premium)} />
            <StatTile label="Policies" value={loading ? "…" : kpis.policies} />
            <StatTile
              label="Est. gross commission"
              value={loading ? "…" : `~${formatMoney(kpis.gross)}`}
              sub="2.5% of premium, before referral splits"
            />
            <StatTile
              label="Splits owed"
              value={loading ? "…" : `~${formatMoney(kpis.owed)}`}
              sub="est. share of referring agents"
            />
          </>
        ) : (
          <>
            <StatTile
              label="Est. commission / yr"
              value={loading ? "…" : `~${formatMoney(kpis.share)}`}
              sub={
                loading
                  ? ""
                  : `across ${kpis.policies} active polic${
                      kpis.policies === 1 ? "y" : "ies"
                    } at today's premiums`
              }
            />
            <StatTile label="Policies paying" value={loading ? "…" : kpis.policies} />
            <StatTile
              label="Avg split"
              value={loading ? "…" : `${kpis.avgSplit}%`}
              sub="across your policies"
            />
          </>
        )}
      </div>

      {/* Filters — staff only; referral stays calm and filter-free (E §7). */}
      {can.seeOtherSplits ? (
        <div className="feat-comm-filters">
          <Segmented
            options={LOB_OPTIONS}
            value={lob}
            onChange={setLob}
            aria-label="Line of business"
          />
          <div className="feat-comm-filters__agent">
            <Combobox
              options={agentOptions}
              value={agentFilter}
              onChange={(v) => setAgentFilter(v)}
              placeholder="Filter by agent…"
            />
          </div>
        </div>
      ) : null}

      {loading ? (
        <Card>
          <SkeletonText lines={5} />
        </Card>
      ) : !hasRows ? (
        <Card>
          <EmptyState
            icon={<DollarIcon size={32} />}
            title="No commissions yet"
            message={
              can.seeOtherSplits
                ? "Commissions appear here as policies bind."
                : "Commissions appear here as your referrals bind."
            }
          />
        </Card>
      ) : (
        <>
          {/* Staff by-agent rollup — each participant's estimated SHARE (E's
              review: distinct from the gross; tier badge separates the agency's
              own share from payables to referring agents). */}
          {can.seeOtherSplits && byAgent.length > 0 ? (
            <Card title="By agent" className="feat-comm-byagent">
              <DataTable
                columns={[
                  {
                    key: "name",
                    header: "Agent",
                    render: (r) => (
                      <span className="feat-pipeline-name">
                        {r.name}
                        <Badge variant={r.authority === "writing" ? "brand" : "neutral"}>
                          {r.authority === "writing" ? "Writing" : "Referral"}
                        </Badge>
                      </span>
                    )
                  },
                  { key: "policies", header: "Policies", align: "num", render: (r) => r.policies },
                  {
                    key: "commission",
                    header: "Est. share",
                    align: "num",
                    sortValue: (r) => r.commission,
                    render: (r) => `~${formatMoney(r.commission)}`
                  }
                ]}
                rows={byAgent}
                rowKey={(r) => r.name}
                countLabel="agents"
              />
              {/* E's review: a tie-out so the Splits-owed KPI is visibly the
                  referral portion of gross, and writer rows read as house share. */}
              <p className="feat-comm-reconcile">
                Gross ~{formatMoney(kpis.gross)} = referral splits ~{formatMoney(kpis.owed)} +
                agency share ~{formatMoney(Math.max(0, kpis.gross - kpis.owed))}.
              </p>
            </Card>
          ) : null}

          {/* By-month rollup (E §7 primary view): expand a month for its lines. */}
          <div className="feat-comm-months-label">By effective month</div>
          <div className="feat-comm-months">
            {months.map((group) => (
              <MonthGroup
                key={group.key}
                group={group}
                expanded={expanded.has(group.key)}
                onToggle={() => toggleMonth(group.key)}
                renderLine={renderLine}
              />
            ))}
          </div>

          <p className="feat-d2row__footnote">
            ≈ estimates assume 2.5% of current premium — your commission statement is the source of
            truth.
            {kpis.excluded > 0
              ? ` ${kpis.excluded} polic${kpis.excluded === 1 ? "y is" : "ies are"} excluded from totals for an implausible premium on file.`
              : ""}
          </p>
        </>
      )}
    </div>
  );
}

export default CommissionsPage;
